/** Convert #rrggbb → "H S% L%" (format used by Tailwind CSS variables) */
export function hexToHsl(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Returns "0 0% 0%" (black) or "0 0% 100%" (white) depending on perceived brightness */
function contrastForeground(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (r * 299 + g * 587 + b * 114) / 1000;
  return luminance >= 128 ? '0 0% 9%' : '0 0% 100%';
}

function contrastHex(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (r * 299 + g * 587 + b * 114) / 1000;
  return luminance >= 128 ? '#111827' : '#ffffff';
}

// DS v1.6 dark palette — inline overrides para vencer applyTheme()
// Alinhado com juri_pagamentos/globals.css dark block (fonte canônica)
const DARK_VARS = {
  '--background':             '100 5% 10%',   // #1A1C19
  '--foreground':             '30 13% 91%',   // #E8E6E1
  '--card':                   '100 5% 13%',   // #24261F
  '--card-foreground':        '30 13% 91%',
  '--popover':                '100 5% 13%',
  '--popover-foreground':     '30 13% 91%',
  '--primary':                '41 86% 49%',   // #E1A819
  '--primary-foreground':     '135 5% 12%',   // #1F2220
  '--secondary':              '80 7% 18%',    // #2E302A
  '--secondary-foreground':   '30 13% 91%',
  '--muted':                  '80 7% 18%',    // #2E302A (era #2A2C27 — corrigido)
  '--muted-foreground':       '30 7% 63%',    // #A8A399
  '--accent':                 '80 7% 18%',
  '--accent-foreground':      '30 13% 91%',
  '--border':                 '90 5% 22%',    // #3A3C35
  '--input':                  '73 6% 28%',    // #4A4C43 (era mesmo que border — corrigido)
  '--ring':                   '46 83% 57%',   // #EBBA3F accent-300 (era escuro #A86A00 — corrigido)
  '--destructive':            '351 94% 71%',  // #FB7185 (era escuro 346 64% 43% — corrigido)
  '--destructive-foreground': '100 5% 10%',   // #1A1C19 tinta escura sobre pink claro
  '--status-success':         '149 72% 67%',  // #6EE7A8
  '--status-success-bg':      '149 40% 11%',  // #11281C
  '--status-warning':         '45 85% 61%',   // #F0C548
  '--status-warning-bg':      '40 49% 12%',   // #2E2410
  '--status-info':            '221 100% 79%', // #93B4FF
  '--status-info-bg':         '220 50% 16%',  // #15233F
  '--status-error':           '351 94% 71%',  // #FB7185
  '--status-error-bg':        '349 42% 13%',  // #2E1318
  '--surface-sidebar':        '100 5% 8%',    // #141512
  '--surface-sidebar-hover':  '100 5% 13%',   // #24261F
  // layout surface (hex — usados diretamente como background-color)
  '--page-bg':    '#1A1C19',  // fundo geral dark
  '--sidebar-bg': '#141512',  // sidebar dark
  '--footer-bg':  '#24261F',  // footer dark (card level)
  '--footer-fg':  '#A8A399',  // footer text dark (muted-foreground)
  // chart tokens dark
  '--chart-1': '41 86% 49%',   // #E1A819
  '--chart-2': '222 100% 74%', // #7AA2FF
  '--chart-3': '145 56% 59%',  // #5BD08B
  '--chart-4': '261 85% 77%',  // #B794F6
  '--chart-5': '174 59% 57%',  // #4FD1C5
  '--chart-6': '351 94% 71%',  // #FB7185
};

function applyDarkVars(root) {
  Object.entries(DARK_VARS).forEach(([k, v]) => root.style.setProperty(k, v));
}

function removeDarkVars(root) {
  Object.keys(DARK_VARS).forEach(k => root.style.removeProperty(k));
}

export function applyDarkVarsPublic() { applyDarkVars(document.documentElement); }
export function removeDarkVarsPublic() { removeDarkVars(document.documentElement); }

/** Apply a theme config object to :root CSS variables (DS v1.6) */
export function applyTheme(config) {
  const root = document.documentElement;

  // Primary (gold) — primary-foreground via contrastForeground (tinta escura sobre gold)
  root.style.setProperty('--primary',            hexToHsl(config.primary_hex));
  root.style.setProperty('--primary-foreground', contrastForeground(config.primary_hex));
  // Ring = accent-700 (38 100% 33%) — fixo DS v1.6 (4.4:1 WCAG non-text)
  root.style.setProperty('--ring', '38 100% 33%');

  // Status tokens — usa defaults DS se não fornecidos pelo tenant
  root.style.setProperty('--accord-green',    hexToHsl(config.highlight_hex || THEME_DEFAULTS.highlight_hex));
  root.style.setProperty('--cessao-yellow',   hexToHsl(config.accent_hex    || THEME_DEFAULTS.accent_hex));

  // Layout surface variables
  const sidebarBg = config.sidebar_bg || THEME_DEFAULTS.sidebar_bg;
  root.style.setProperty('--sidebar-bg',   sidebarBg);
  root.style.setProperty('--header-bg',    config.header_bg || THEME_DEFAULTS.header_bg);
  root.style.setProperty('--header-fg',    contrastHex(config.header_bg || THEME_DEFAULTS.header_bg));
  root.style.setProperty('--footer-bg',    config.footer_bg || THEME_DEFAULTS.footer_bg);
  root.style.setProperty('--footer-fg',    contrastHex(config.footer_bg || THEME_DEFAULTS.footer_bg));
  root.style.setProperty('--page-bg',      config.page_bg   || THEME_DEFAULTS.page_bg);
  root.style.setProperty('--background',   hexToHsl(config.page_bg || THEME_DEFAULTS.page_bg));

  // Border radius
  const r = config.border_radius_px ?? THEME_DEFAULTS.border_radius_px;
  root.style.setProperty('--radius',    `${r}px`);
  root.style.setProperty('--radius-sm', `${Math.max(4, Math.round(r * 0.67))}px`);

  // Sidebar foreground — DS v1.6: charcoal sidebar, texto branco com hover #424239
  const st = config.sidebar_text || '#ffffff';
  root.style.setProperty('--sidebar-fg',        hexToRgba(st, 0.7));
  root.style.setProperty('--sidebar-fg-active', st);
  root.style.setProperty('--sidebar-hover-bg',  '#424239');
  root.style.setProperty('--sidebar-border',    hexToRgba(st, 0.1));

  // Dark mode: preferência localStorage > prefers-color-scheme se habilitado no tema
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('theme') : null;
  let darkActive = false;
  if (stored === 'dark') {
    darkActive = true;
  } else if (stored === 'light') {
    darkActive = false;
  } else if (config.dark_mode_enabled) {
    darkActive = typeof window !== 'undefined'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false;
  }
  document.documentElement.classList.toggle('dark', darkActive);
  document.documentElement.dataset.theme = darkActive ? 'dark' : 'light';

  // Dark overrides como inline styles — necessário pois applyTheme já setou vars
  // light como inline styles (que vencem qualquer seletor .dark no CSS).
  if (darkActive) {
    applyDarkVars(root);
  } else {
    removeDarkVars(root);
  }

  // Favicon dinâmico
  const faviconHref = config.favicon_url || config.logo_url;
  if (faviconHref) {
    const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
    link.rel = 'icon';
    link.href = faviconHref;
    if (!link.parentNode) document.head.appendChild(link);
  }
}

export const DEFAULT_FORM_SECTIONS = [
  'dados_pessoais',
  'dados_pessoais_extra',
  'endereco',
  'dados_bancarios',
  'valores',
  'condicoes_cessao',
  'envio_documentos',
];

export const THEME_DEFAULTS = {
  primary_hex:      '#D28B00',  // accent-600 DS v1.6
  accent_hex:       '#EBBA3F',  // accent-300 (compat cessao-yellow)
  highlight_hex:    '#15803D',  // status-success
  header_bg:        '#ffffff',
  sidebar_bg:       '#373A36',  // brand-charcoal DS v1.6
  sidebar_text:     '#ffffff',
  footer_bg:        '#ffffff',
  page_bg:          '#F8FAFC',  // DS v1.6 background
  border_radius_px: 12,         // DS v1.6 radius
};
